// Libraries
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _noop from 'lodash-es/noop';
import useSubscription from './useSubscription';

// constants
// import {

// } from '../pubsubTopics';
import ZpaykitFallback from './ZpaykitFallback';
import {
  WIDGET_STATUS,
  VALIDATION_STATUS,
  ZPAYKIT_ERROR,
  INITIATE_CHECKOUT,
  END_CHECKOUT,
  CONTINUE_CHECKOUT_MAKE_PAYMENT,
  CONTINUE_CHECKOUT_PLACE_ORDER,
  PAYMENT_ORDER_PARAMETERS,
  INTERNAL_CHECKOUT_CLICKED,
  INITIALIZE_KIT,
  UPI_PAYMENT_IN_PROGRESS,
} from './pubsubTopics';
import { ZPAYKT_BASE_URL } from '../../../../constants/urls';

// styled compoents
const PaymentWindow = styled.iframe`
  border: 0;
  width: 100%;
  height: 850px;
  transition: 0.5s height;
  }
`;

const ZpayKitLoader = ({
  onWidgetLoad,
  onValidationStatusChange,
  onZpayKitError,
  onPlaceOrder,
  onInternalCheckoutClick,
  isCheckoutEnded,
  isCheckoutInititated,
  initiateCheckoutParams,
  isPlaceOrderSuccessful,
  makePaymentParams,
  zpaykitArgs,
  kitInitParams,
  accessCode,
  onUpiPaymentProgress,
}) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(true);
  const [isKitLoaded, setIsKitLoaded] = useState(false);

  const hideIframeLoader = () => setIsIframeLoaded(false);
  const setPaymentKitLoaded = () => setIsKitLoaded(true);

  // ---- publish ----
  useEffect(() => {
    if (isKitLoaded && accessCode) {
      window.zpayapi.publish(INITIALIZE_KIT, kitInitParams);
    }
  }, [isKitLoaded, accessCode]);

  useEffect(() => {
    if (isKitLoaded && isCheckoutEnded) {
      window.zpayapi.publish(END_CHECKOUT, {});
    }
  }, [isKitLoaded, isCheckoutEnded]);

  useEffect(() => {
    if (isCheckoutInititated) {
      window.zpayapi.publish(INITIATE_CHECKOUT, initiateCheckoutParams);
    }
  }, [isCheckoutInititated, initiateCheckoutParams]);

  useEffect(() => {
    if (isPlaceOrderSuccessful) {
      window.zpayapi.publish(CONTINUE_CHECKOUT_MAKE_PAYMENT, makePaymentParams);
    }
  }, [isPlaceOrderSuccessful]);

  useEffect(() => {
    if (isKitLoaded) {
      window.zpayapi.publish(PAYMENT_ORDER_PARAMETERS, zpaykitArgs);
    }
  }, [isKitLoaded, zpaykitArgs]);

  // ----- subscriptions -----
  const onUpiPaymentProgressCallback = useCallback(() => {
    onUpiPaymentProgress();
  }, [onUpiPaymentProgress]);

  const widgetLoadCallback = useCallback(() => {
    onWidgetLoad();
    setPaymentKitLoaded();
  }, [onWidgetLoad]);

  const validationStatusChangeCallback = useCallback(
    ({ status, paymentParams }) =>
      onValidationStatusChange(status, paymentParams),
    [onValidationStatusChange],
  );

  const onZpayKitErrorCallback = useCallback(
    ({ errorType, errorMessage }) => {
      onZpayKitError(errorType, errorMessage);
    },
    [onZpayKitError],
  );

  const onPlaceOrderCallback = useCallback(
    ({
      success,
      errorMessage = 'Place order could not be initiated.',
      ...additionalPaymentParams
    }) => {
      onPlaceOrder(success, errorMessage, additionalPaymentParams);
    },
    [onPlaceOrder],
  );

  const onInternalCheckoutClickCallback = useCallback(() => {
    onInternalCheckoutClick();
  }, [onInternalCheckoutClick]);

  useSubscription(INTERNAL_CHECKOUT_CLICKED, onInternalCheckoutClickCallback);
  useSubscription(CONTINUE_CHECKOUT_PLACE_ORDER, onPlaceOrderCallback);
  useSubscription(ZPAYKIT_ERROR, onZpayKitErrorCallback);
  useSubscription(WIDGET_STATUS, widgetLoadCallback);
  useSubscription(VALIDATION_STATUS, validationStatusChangeCallback);
  useSubscription(UPI_PAYMENT_IN_PROGRESS, onUpiPaymentProgressCallback);

  return (
    <>
      <PaymentWindow
        id='payment_widget'
        src={`${ZPAYKT_BASE_URL}/init`}
        onLoad={hideIframeLoader}
      />
      {isIframeLoaded && <ZpaykitFallback />}
    </>
  );
};

ZpayKitLoader.propTypes = {
  onUpiPaymentProgress: PropTypes.func,
  onWidgetLoad: PropTypes.func,
  onValidationStatusChange: PropTypes.func.isRequired,
  onZpayKitError: PropTypes.func.isRequired,
  onPlaceOrder: PropTypes.func.isRequired,
  onInternalCheckoutClick: PropTypes.func.isRequired,
  isCheckoutInititated: PropTypes.bool.isRequired,
  initiateCheckoutParams: PropTypes.objectOf(PropTypes.any),
  isPlaceOrderSuccessful: PropTypes.bool.isRequired,
  makePaymentParams: PropTypes.objectOf(PropTypes.any).isRequired,
  zpaykitArgs: PropTypes.objectOf(PropTypes.any).isRequired,
  kitInitParams: PropTypes.shape({
    service_type: PropTypes.string.isRequired,
    order_type: PropTypes.string.isRequired,
    consumer: PropTypes.string.isRequired,
    version: PropTypes.number.isRequired,
    country_id: PropTypes.number.isRequired,
    user_id: PropTypes.string,
    code: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
  }).isRequired,
  isCheckoutEnded: PropTypes.bool.isRequired,
  accessCode: PropTypes.string.isRequired,
};

ZpayKitLoader.defaultProps = {
  onUpiPaymentProgress: _noop,
  initiateCheckoutParams: {},
  onWidgetLoad: _noop,
};

export default ZpayKitLoader;
